import { memo, type CSSProperties, MutableRefObject } from 'react';
import type { PageBackground, Element, GridGuideSettings, Page } from '@/components/canvas/document/publicTypes';
import { GridGuideLayer } from '@/components/canvas/workspace/overlays';
import { GroupElement } from '@/components/canvas/elements/registry/elementComponents';
import type { GroupEditContext } from '@/components/canvas/elements/registry/elementComponents';
import type {
  GroupElement as GroupElementType,
  ImageElement as ImageElementType,
} from '@/components/canvas/document/publicTypes';
import { getBuiltinElementComponents } from '@/components/canvas/elements/registry/builtinElements';
import type { WorkspaceInteractionApi } from '@/components/canvas/workspace/controllers/useWorkspaceInteraction';
import { PageChrome } from './PageChrome';
import {
  PAGE_CHROME_MIN_SCREEN_WIDTH,
  pageChromeWorldHeight,
} from '@/components/canvas/workspace/model/pageArrangement';
import { InteractionLayer } from '@/components/canvas/workspace/layers/InteractionLayer';
import { BlockView } from '@/components/canvas/blocks/BlockView';
import { BlockChromeLayer } from '@/components/canvas/blocks/BlockChromeLayer';
import type { Block } from '@/components/canvas/blocks/types';
import type { BlockDisplayLayout } from '@/components/canvas/blocks/registry/types';
import '@/components/canvas/blocks';

/** Zustand 选择器勿内联 `[]`，否则会每次产生新引用并触发无限重渲染 */
const EMPTY_BLOCK_IDS: string[] = [];

export interface PageSurfaceProps {
  page: Page;
  isActive: boolean;
  isPageDragging: boolean;
  grid: GridGuideSettings;
  guidesVisible: boolean;
  interaction: WorkspaceInteractionApi | null;
  /** 全局选区（含跨页框选时非激活页上的元素） */
  selectedElementIds: string[];
  zoom: number;
  pageRef: MutableRefObject<HTMLDivElement | null>;
  setPageElementRef?: (el: HTMLDivElement | null) => void;
  pageIndex: number;
  pageCount: number;
  onDragHandleMouseDown: (e: React.MouseEvent) => void;
  onChromeClick: (e: React.MouseEvent) => void;
  onInactivePagePointerDown?: (e: React.MouseEvent) => void;
  /** 工作区拖入页内预览（非激活页也需显示） */
  workspaceDragPreviewOnPage?: { pageId: string; elements: Element[] } | null;
  /** 跨页/拖出工作区时在源页隐藏被拖元素（含非激活源页） */
  hideDraggedElementIds?: Set<string>;
  /** 演示态隐藏页头并禁用页内交互层 */
  isPresentationMode?: boolean;
  /** 只读态禁用页内编辑交互；演示态必然只读 */
  isReadonlyMode?: boolean;
}

function pageBackgroundStyle(background: PageBackground): CSSProperties {
  const style: CSSProperties = {};
  if (background.type === 'color') style.backgroundColor = background.value ?? '#ffffff';
  else if (background.type === 'gradient' && background.value) style.background = background.value;
  else if (background.type !== 'image') style.backgroundColor = '#ffffff';
  return style;
}

function PageSurfaceInner({
  page,
  isActive,
  isPageDragging,
  grid,
  guidesVisible,
  interaction,
  selectedElementIds,
  zoom,
  pageRef,
  setPageElementRef,
  pageIndex,
  pageCount,
  onDragHandleMouseDown,
  onChromeClick,
  onInactivePagePointerDown,
  workspaceDragPreviewOnPage: workspaceDragPreviewOnPageProp = null,
  hideDraggedElementIds,
  isPresentationMode = false,
  isReadonlyMode = false,
}: PageSurfaceProps) {
  const { workspaceLayout, size, background } = page;
  const safeZoom = zoom > 0 ? zoom : 1;
  const columnWidth = Math.max(size.width, PAGE_CHROME_MIN_SCREEN_WIDTH / safeZoom);
  const elementComponents = getBuiltinElementComponents();
  const activeInteraction = isActive && interaction && !isReadonlyMode ? interaction : null;

  const elements = activeInteraction ? activeInteraction.elements : page.elements;
  const blocks = page.blocks ?? [];
  const visibleElements = elements.filter((el) => el.interaction.visible !== false);
  const rootElements = visibleElements.filter((el) => !el.layout.parentId);

  const selectedBlockIds = activeInteraction?.selectedBlockIds ?? EMPTY_BLOCK_IDS;
  const blockDragOffset = activeInteraction?.blockDragOffset ?? { dx: 0, dy: 0 };
  const draggingBlockIds = new Set(
    activeInteraction?.blockDrag?.startPositions.map((p) => p.id) ?? [],
  );
  const blockResizePreview = activeInteraction?.blockResizePreview ?? null;
  const blockResizeId = activeInteraction?.blockResizeState?.id ?? null;

  const resolveBlockDisplayLayout = (blockId: string, layout: Block['layout']): BlockDisplayLayout => {
    const isDragging = draggingBlockIds.has(blockId);
    const usePreview = blockResizeId === blockId && blockResizePreview;
    return {
      x: (usePreview ? blockResizePreview.x : layout.x) + (isDragging ? blockDragOffset.dx : 0),
      y: (usePreview ? blockResizePreview.y : layout.y) + (isDragging ? blockDragOffset.dy : 0),
      width: usePreview ? blockResizePreview.width : layout.width,
      height: usePreview ? blockResizePreview.height : layout.height,
    };
  };

  const editingGroupId = activeInteraction?.editingGroupId ?? null;
  const editingTextId = activeInteraction?.editingTextId ?? null;
  const elementDrag = activeInteraction?.elementDrag ?? null;
  const dragOffset = activeInteraction?.dragOffset ?? { dx: 0, dy: 0 };
  const hideSelectionChrome = isReadonlyMode || (activeInteraction?.hideSelectionChrome ?? false);
  const workspaceDragPreviewOnPage =
    isReadonlyMode
      ? null
      : (workspaceDragPreviewOnPageProp ?? interaction?.workspaceDragPreviewOnPage ?? null);
  const dropPreviewElements =
    workspaceDragPreviewOnPage?.pageId === page.id
      ? workspaceDragPreviewOnPage.elements
      : null;

  const draggingElementIds = new Set(
    elementDrag?.startPositions.map((p) => p.id) ?? [],
  );
  const hideDraggedOnPage = (id: string) => hideDraggedElementIds?.has(id) ?? false;

  const pageHandlers =
    activeInteraction
      ? {
          onClick: activeInteraction.handleCanvasClick,
          onDoubleClick: activeInteraction.handleCanvasDoubleClick,
          onMouseDown: activeInteraction.handleCanvasMouseDown,
          onContextMenu: activeInteraction.handleContextMenu,
        }
      : {};

  const noop = (e: React.MouseEvent) => e.stopPropagation();

  const groupEditCtx: GroupEditContext | null =
    activeInteraction
      ? {
          editingGroupId,
          selectedIds: selectedElementIds,
          dragOffset: elementDrag ? dragOffset : { dx: 0, dy: 0 },
          editingTextId,
          onChildMouseDown: (e, child) => activeInteraction.handleElementHitMouseDown(e, child),
          onStartImagePositionDrag: activeInteraction.handleStartImagePositionDrag,
          onChildStartEdit: (id, opts) => activeInteraction.beginTextEdit(id, opts),
        }
      : null;

  return (
    <div
      className="absolute flex flex-col"
      style={{
        left: workspaceLayout.x,
        top: isPresentationMode ? workspaceLayout.y : workspaceLayout.y - pageChromeWorldHeight(safeZoom),
        width: columnWidth,
        zIndex: workspaceLayout.zIndex,
        // 演示态须保持 visible，否则 content-visibility:auto 会暂停页内计时等更新
        contentVisibility: isPresentationMode || isActive ? 'visible' : 'auto',
      }}
    >
      {!isPresentationMode ? (
        <PageChrome
          page={page}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageWidth={size.width}
          zoom={zoom}
          isActive={isActive}
          isDragging={isPageDragging}
          onDragHandleMouseDown={onDragHandleMouseDown}
          onChromeClick={onChromeClick}
          readonly={isReadonlyMode}
        />
      ) : null}
      <div
        ref={setPageElementRef}
        data-page={page.id}
        data-page-active={isActive ? '' : undefined}
        className="relative"
        style={{ width: size.width, height: size.height }}
        onMouseDownCapture={!isActive && !isReadonlyMode ? onInactivePagePointerDown : undefined}
        {...pageHandlers}
      >
        {/* 页内元素裁切；选区层在裁切外，避免页框描边/尺寸标签被 overflow-hidden 裁掉 */}
        <div
          className="absolute inset-0 overflow-hidden shadow-lg"
          style={pageBackgroundStyle(background)}
        >
        {background.type === 'image' && background.value && (
          <div
            className="pointer-events-none absolute inset-0 bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url(${background.value})`,
              backgroundColor: background.backgroundColor ?? '#fff',
              backgroundPosition: `${background.imagePositionX ?? 50}% ${background.imagePositionY ?? 50}%`,
            }}
          />
        )}

        <GridGuideLayer
          width={size.width}
          height={size.height}
          grid={grid}
          visible={guidesVisible && !isReadonlyMode}
        />

        {rootElements
          .filter((el) => !hideDraggedOnPage(el.id))
          .slice()
          .sort((a, b) => a.layout.zIndex - b.layout.zIndex)
          .map((el) => {
            const selected =
              !hideSelectionChrome && selectedElementIds.includes(el.id);
            const isBeingDragged =
              isActive && elementDrag != null && draggingElementIds.has(el.id);
            const offset = isBeingDragged ? dragOffset : { dx: 0, dy: 0 };
            if (el.type === 'group') {
              const isEditingThisGroup = isActive && editingGroupId === el.id;
              const childDragOffset =
                isEditingThisGroup && elementDrag ? dragOffset : { dx: 0, dy: 0 };
              return (
                <GroupElement
                  key={el.id}
                  element={el as GroupElementType}
                  zoom={zoom}
                  selected={isEditingThisGroup ? false : selected}
                  dragOffset={isEditingThisGroup ? { dx: 0, dy: 0 } : offset}
                  onSelect={noop}
                  onMouseDown={
                    activeInteraction
                      ? (e, elem) => activeInteraction.handleElementHitMouseDown(e, elem)
                      : noop
                  }
                  allElements={elements}
                  selectElement={activeInteraction?.selectElement ?? (() => {})}
                  isEditingGroup={isEditingThisGroup}
                  selectedChildIds={isEditingThisGroup ? selectedElementIds : []}
                  childDragOffset={childDragOffset}
                  editingTextId={isEditingThisGroup ? editingTextId : null}
                  onChildMouseDown={
                    activeInteraction
                      ? (e, child) => activeInteraction.handleElementHitMouseDown(e, child)
                      : noop
                  }
                  onChildStartEdit={
                    activeInteraction
                      ? (id, opts) => activeInteraction.beginTextEdit(id, opts)
                      : () => {}
                  }
                  groupEditContext={groupEditCtx ?? undefined}
                />
              );
            }
            const Comp = elementComponents[el.type as keyof typeof elementComponents];
            if (!Comp) return null;
            const isText = el.type === 'text';
            if (isActive && isText && el.id === editingTextId) return null;
            return (
              <Comp
                key={el.id}
                element={el as never}
                zoom={zoom}
                selected={selected}
                dragOffset={offset}
                onSelect={
                  isActive && isText && activeInteraction
                    ? (e: React.MouseEvent) => {
                        e.stopPropagation();
                        activeInteraction.selectElement(el.id, false);
                      }
                    : noop
                }
                onMouseDown={
                  activeInteraction
                    ? (e: React.MouseEvent, elem: Element) =>
                        activeInteraction.handleElementHitMouseDown(e, elem)
                    : noop
                }
                {...(isActive && isText && activeInteraction
                  ? {
                      onStartEdit: (id: string, opts?: import('@/components/canvas/elements/types').TextEditOptions) => {
                        if (el.interaction.locked) return;
                        activeInteraction.beginTextEdit(id, opts);
                      },
                    }
                  : {})}
                {...(isActive &&
                  el.type === 'image' &&
                  activeInteraction && {
                    onStartImagePositionDrag: (elem: ImageElementType, ev: React.MouseEvent) =>
                      activeInteraction.handleStartImagePositionDrag(elem, ev),
                  })}
              />
            );
          })}

        {dropPreviewElements && dropPreviewElements.length > 0 && (
          <div className="pointer-events-none absolute inset-0 z-[10]" aria-hidden>
            {dropPreviewElements
              .slice()
              .sort((a, b) => a.layout.zIndex - b.layout.zIndex)
              .map((el) => {
                const Comp = elementComponents[el.type as keyof typeof elementComponents];
                if (!Comp) return null;
                return (
                  <Comp
                    key={`drop-preview-${el.id}`}
                    element={el as never}
                    selected={false}
                    dragOffset={{ dx: 0, dy: 0 }}
                    onSelect={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                );
              })}
          </div>
        )}
        </div>

        {blocks.length > 0 ? (
          <div
            className="pointer-events-none absolute inset-0 z-[6] overflow-hidden"
            style={{ contentVisibility: 'visible' }}
            aria-hidden={isPresentationMode ? true : undefined}
          >
            {blocks
              .slice()
              .sort((a, b) => a.layout.zIndex - b.layout.zIndex)
              .map((block) => {
                const displayLayout = resolveBlockDisplayLayout(block.id, block.layout);
                return (
                  <BlockView
                    key={block.id}
                    block={block}
                    displayLayout={displayLayout}
                    isPresentationMode={isPresentationMode}
                    onPointerDown={
                      activeInteraction
                        ? (e, b) => activeInteraction.handleBlockHitPointerDown(e, b)
                        : undefined
                    }
                  />
                );
              })}
          </div>
        ) : null}

        {activeInteraction ? (
          <>
            <InteractionLayer
              embedded
              size={size}
              zoom={zoom}
              pageRef={pageRef}
              interaction={activeInteraction}
            />
            <BlockChromeLayer
              blocks={blocks}
              selectedBlockIds={selectedBlockIds}
              blockDragOffset={blockDragOffset}
              resizePreview={blockResizePreview}
              resizeState={activeInteraction.blockResizeState ?? null}
              zoom={zoom}
              hideChrome={hideSelectionChrome}
              onStartBlockResize={activeInteraction.startBlockResize}
            />
          </>
        ) : null}

      </div>
    </div>
  );
}

export const PageSurface = memo(PageSurfaceInner);
